import type { ModelViewerBackgroundCallback, ModelViewerEnvironmentMapCallback, ModelViewerExposureCallback, ModelViewerOrientationCallback } from "@centricsoftware/model-viewer";
import comm from "csi/comm";
import PostRequest from "csi/comm/PostRequest";
import html from "csi/html";
import locale from "csi/locale";
import { submitWriter } from "csi/service/writerService";
import { normalizeExposure } from "csi/threeD/snapshotTemplates/snapshotUtils";
import { C8Node, ICache, cnl } from "csi/typings";
import { getFileNameExtension } from "csi/utility/fileUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import ErrorDialog from "designSystem/components/dialog/ErrorDialog";
import MessageDialog from "designSystem/components/dialog/MessageDialog";
import _ from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { getFileVaultRequestUrl } from "src/c8-platform/core/request";
import Cache from "../Cache";
import { rightPaneStore } from "../rootStore";
import CanvasStore from "./canvasStore";

const CENTRIC_STUDIO_ENVIRONMENT = "CentricStudio.hdr";

export default class EnvironmentsStore extends ReactionStore {
	/** stores the cache that is passed in from the constructor of the threeDViewerStore */
	private readonly sourceViewCache: ICache;

	/** cache of the threeDViewerStore */
	threeDViewerCache: Cache;

	/** Stores document url */
	docUrl: cnl;

	/** store for canvas state */
	@observable.ref canvasStore: CanvasStore;

	/** Entry point for ModelViewer setEnvironmentMap  */
	@observable.ref modelViewerUpdateEnvironment: ModelViewerEnvironmentMapCallback;

	/** Entry point for ModelViewer toneExposure */
	@observable.ref modelViewerUpdateExposure: ModelViewerExposureCallback;

	/** Entry point for ModelViewer orientation */
	@observable.ref modelViewerUpdateOrientation: ModelViewerOrientationCallback;

	/** Entry point for ModelViewer update background */
	@observable.ref modelViewerUpdateBackground: ModelViewerBackgroundCallback;

	/** returns the document node */
	get docNode(): C8Node {
		return this.threeDViewerCache.get(this.docUrl);
	}

	/** get the environment Urls */
	@computed get environmentUrls(): cnl[] {
		return this.threeDViewerCache.data.filter((node) => node.$Type === "ThreeDEnvironment").map((node) => node.$URL);
	}

	/** Get environments data */
	@computed get environments(): C8Node[] {
		// order the environment nodes so that 'CentricStudio' is always first
		const environments = this.threeDViewerCache.getNodes(this.environmentUrls);
		const { centricStudio, otherEnvironments } = environments.reduce(
			(acc, env) => {
				if (env.$Name === CENTRIC_STUDIO_ENVIRONMENT) {
					acc.centricStudio = env;
				} else {
					acc.otherEnvironments.push(env);
				}
				return acc;
			}
			, { centricStudio: null, otherEnvironments: [] },
		);

		return centricStudio ? [centricStudio, ...otherEnvironments] : environments;
	}

	/** return the default environment */
	@computed get defaultEnvironmentURL(): cnl | undefined {
		return this.environmentUrls.find((url) => this.threeDViewerCache.get(url).Default);
	}

	/** Get the Centric Studio predefined environment URL */
	get centricStudioEnvironmentURL(): cnl | undefined {
		return this.environments.find((env) => env.$Name === CENTRIC_STUDIO_ENVIRONMENT)?.$URL;
	}

	/** Determine if the selected environment is the centeric studio environment */
	get isSelectedEnvirionmentCentricStudio(): boolean {
		return this.selectedEnvironmentUrl === this.centricStudioEnvironmentURL;
	}

	/** Get environment nodes for the selected environment */
	get selectedEnvironmentNode(): C8Node {
		return this.threeDViewerCache.get(this.environmentUrls[this.selectedEnvironmentUrl]);
	}

	/** Reset the environment lighting to the default map, exposure and background values */
	@action resetToDefaultLighting = (): void => {
		const {
			defaultEnvironmentURL,
			handleEnvironmentSelectChange,
		} = this;
		handleEnvironmentSelectChange(null, defaultEnvironmentURL);
	};

	/** update selected environment url */
	@action handleEnvironmentSelectChange = (event: React.ChangeEvent<HTMLInputElement>, value: cnl): void => {
		this.updateSelectedEnvironmentUrl(value);
	};

	/** set selected environment */
	@action updateSelectedEnvironmentUrl = (value?: cnl): void => {
		this.canvasStore.selectedEnvironmentUrl = value || null;
		if (!value) {
			this.updateEnvironmentMap();
		}
		else {
			const fullUrl = this.generateEnvironmentUrl(value);
			const selectedEnvironment = this.threeDViewerCache.get(value);
			this.updateEnvironmentMap(fullUrl);
			this.updateEnvironmentBackground(this.canvasStore.environmentBackground || false);
			this.updateEnvironmentExposure(selectedEnvironment?.Exposure);
			this.updateEnvironmentOrientation(selectedEnvironment?.Orientation);
		}
	};

	/** generate environment url */
	generateEnvironmentUrl = (value: cnl): string => {
		const selectedEnvironment = this.threeDViewerCache.get(value);
		return selectedEnvironment ? getFileVaultRequestUrl(selectedEnvironment.EnvironmentFile, false) : null;
	};

	/** reset the user3DEnvironment settings back to the original values on the threeDEnvironment node */
	@action resetUserEnvironmentSettings = async (): Promise<void> => {
		const {
			threeDViewerCache,
			saveEnvironmentChanges,
			selectedEnvironmentUrl,
			updateEnvironmentExposure,
			updateEnvironmentOrientation,
		} = this;

		try {
			const selectedEnvironment = threeDViewerCache.get(selectedEnvironmentUrl);

			updateEnvironmentExposure(selectedEnvironment.Exposure);
			updateEnvironmentOrientation(selectedEnvironment.Orientation);

			await saveEnvironmentChanges();
		} catch (e) {
			rightPaneStore.openDialog(ErrorDialog, {});
		}
	};

	/** debounce the exposure update to ModelViewer */
	updateViewerExposure = _.debounce((value: number): void => {
		const normalizedExposure = normalizeExposure(value);
		this.modelViewerUpdateExposure?.(normalizedExposure);
	}, 50);

	/** debounce the orientation update to ModelViewer */
	updateViewerOrientation = _.debounce((value: number): void => {
		this.modelViewerUpdateOrientation?.(value);
	}, 50);

	/** set environment map*/
	@action updateEnvironmentMap = (value? : cnl | undefined): void => {
		this.modelViewerUpdateEnvironment?.(value);
	};

	/** set environment exposure */
	@action updateEnvironmentExposure = (value: number): void => {
		this.canvasStore.environmentExposure = value;
		this.updateViewerExposure(value);
	};

	/** set environment directional light orientation */
	@action updateEnvironmentOrientation = (value: number = 0): void => {
		this.canvasStore.environmentOrientation = value;
		this.updateViewerOrientation(value);
	};

	/** set scene background to environment */
	@action updateEnvironmentBackground = (value: boolean): void => {
		this.canvasStore.environmentBackground = value;
		this.modelViewerUpdateBackground?.(value);
	};

	/** delete given environment */
	deleteEnvironment = async (environmentUrl: cnl): Promise<void> => {
		const { selectNewEnvironment, environments, selectedEnvironmentUrl } = this;
		const environmentIndex = _.findIndex(environments, (env) => env.$URL === environmentUrl);
		const writer = new Writer();
		writer.deleteNode(environmentUrl);

		try {
			const response = await submitWriter(writer);

			runInAction(() => {
				if (selectedEnvironmentUrl === environmentUrl) {
					selectNewEnvironment(environmentIndex);
				}
				// TODO: Add this check back once the server allows for the default environment to be deleted
				// if (environmentUrl === this.defaultEnvironmentURL) {
				// 	await this.toggleEnvironmentAsDefault(this.centricStudioEnvironmentURL);
				// }
				this.threeDViewerCache.cacheResponse(response);
				this.sourceViewCache.cacheResponse(response);
			});
		} catch (e) {
			console.error("Error deleting environment: ", e);
		}
	};

	/** Set or remove Environment as default 3D Environment for the user */
	toggleEnvironmentAsDefault = async (environmentUrl: cnl): Promise<void> => {
		const writer = new Writer();
		writer.changeNode(environmentUrl)
			.changeAttribute("Default", xml.ATTR_BOOLEAN, true);

		try {
			const response = await submitWriter(writer);
			runInAction(() => {
				this.threeDViewerCache.cacheResponse(response);
			});
		} catch (e) {
			rightPaneStore.openDialog(ErrorDialog, {});
		}
	};

	/** select new environment after deleting current selction */
	@action selectNewEnvironment = (environmentIndex: number): void => {
		const { updateSelectedEnvironmentUrl, environmentUrls } = this;
		const newEnvironmentUrl = environmentIndex === environmentUrls.length
			? environmentUrls[0]
			: environmentUrls[environmentIndex];

		updateSelectedEnvironmentUrl(newEnvironmentUrl);
	};

	/** if error occurs when deleting Environment, reset the viewer to the first environment */
	closeEnvironmentsErrorDialog = (): void => {
		const { environmentUrls, selectedEnvironmentUrl } = this;

		if (!environmentUrls.includes(selectedEnvironmentUrl) && environmentUrls.length > 0) {
			this.updateSelectedEnvironmentUrl(environmentUrls[0]);
		}
	};

	/** returns the selectedEnvironmentUrl from canvasStore */
	get selectedEnvironmentUrl(): cnl {
		return this.canvasStore.selectedEnvironmentUrl;
	}

	/** returns the selected environment lighting */
	get selectedEnvironment(): C8Node {
		return this.threeDViewerCache.get(this.selectedEnvironmentUrl);
	}

	/** returns the environment exposure from canvasStore */
	get environmentExposure(): number {
		return this.canvasStore.environmentExposure;
	}

	/** returns the environment orientation from canvasStore */
	get environmentOrientation(): number {
		return this.canvasStore.environmentOrientation;
	}

	/** returns the environment background from canvasStore */
	@computed get environmentBackground(): boolean {
		return this.canvasStore.environmentBackground;
	}

	/** return true if there is environment data */
	get hasEnvironments(): boolean {
		return !!this.environments.length;
	}

	/** returns if the selected environment has been modified from the original 3D environment settings */
	@computed get environmentCanReset(): boolean {
		const {
			environmentExposure,
			environmentOrientation,
			selectedEnvironment,
		} = this;

		const exposureIsChanged = selectedEnvironment ? environmentExposure !== selectedEnvironment.Exposure : false;
		const orientationIsChanged = selectedEnvironment ? environmentOrientation !== selectedEnvironment.Orientation : false;

		return exposureIsChanged || orientationIsChanged;
	}

	/** create a new 3D Environment */
	@action onNew3DEnvironment = async (): Promise<void> => {
		const fileList = await html.selectFiles(".hdr");

		try {
			// validate file extension, throw error if not
			const validFile = fileList.some((file) => getFileNameExtension(file.name) === "hdr");

			if (!validFile) {
				rightPaneStore.openDialog(ErrorDialog, {
					messageRes: "Error3DInvalidHdr",
					titleRes: "ThreeDEnvironment",
				});
			} else {
				rightPaneStore.openDialog(MessageDialog, {
					category: "progress",
					messageRes: "SavingInProgress",
					titleRes: "New_Message",
					titleResParams: [locale.resource("ThreeDEnvironment")],
				});

				const response = await new PostRequest("Apparel", "Create3DEnvironment")
					.addFiles(fileList, null, "FormFileFieldName")
					.post();

				runInAction(() => {
					this.threeDViewerCache.cacheResponse(response);
				});

				rightPaneStore.closeDialog();
			}
		} catch (error) {
			rightPaneStore.openDialog(ErrorDialog, {
				messageRes: "RequestErrorMessageAndPotentialReasons",
				titleRes: "ThreeDEnvironment",
			});
		}
	};

	/** Save As New create a new node from the currently selected environment */
	@action saveAsNewEnvironment = async (): Promise<void> => {
		const { 
			selectedEnvironmentUrl,
			environmentExposure,
			environmentOrientation,
			threeDViewerCache,
	 } = this;
		const selectedEnvironment = threeDViewerCache.get(selectedEnvironmentUrl);
		const writer = new Writer();

		try {
			rightPaneStore.openDialog(MessageDialog, {
				category: "progress",
				messageRes: "SavingInProgress",
				titleRes: "New_Message",
				titleResParams: [locale.resource("ThreeDEnvironment")],
			});
			const newEnvName =  locale.resource("NAME_COPY", [selectedEnvironment.$Name]);
			const newEnvironmentUrl = comm.createUrl();
			writer.copyNode(newEnvironmentUrl, selectedEnvironmentUrl);
			writer.changeNode(newEnvironmentUrl)
				.changeAttribute("Node Name", xml.ATTR_STRING, newEnvName)
				.changeAttribute("Exposure", xml.ATTR_DOUBLE, environmentExposure || selectedEnvironment.Exposure)
				.changeAttribute("Orientation", xml.ATTR_DOUBLE, environmentOrientation || selectedEnvironment.Orientation);

			const response = await submitWriter(writer);

			runInAction(() => {
				threeDViewerCache.cacheResponse(response);
				this.updateSelectedEnvironmentUrl(newEnvironmentUrl);
			});

			rightPaneStore.closeDialog();
		} catch (e) {
			rightPaneStore.openDialog(ErrorDialog, {
				messageRes: "RequestErrorMessageAndPotentialReasons",
				titleRes: "ThreeDEnvironment",
			});
		}
	};

	/** save changes of a given environment */
	saveEnvironmentChanges = async (): Promise<void> => {
		const writer = new Writer();
		const { environmentExposure, environmentOrientation, selectedEnvironmentUrl } = this;

		writer.changeNode(selectedEnvironmentUrl)
			.changeAttribute("Exposure", xml.ATTR_DOUBLE, environmentExposure)
			.changeAttribute("Orientation", xml.ATTR_DOUBLE, environmentOrientation);

		await this.saveChanges(writer);
	};

	/** save changes to an environment name */
	saveEnvironmentName = async (node: cnl, newName: string): Promise<void> => {
		const writer = new Writer();

		writer.changeNode(node)
			.changeAttribute("Node Name", xml.ATTR_STRING, newName);

		await this.saveChanges(writer);
	};

	/** Submit writer and cache response */
	private async saveChanges(writer: Writer): Promise<void> {
		try {
			const response = await submitWriter(writer);
			this.threeDViewerCache.cacheResponse(response);
		} catch (e) {
			rightPaneStore.openDialog(ErrorDialog, {
				messageRes: "RequestErrorMessage",
				titleRes: "RequestError",
			});
		}
	}

	constructor(canvasStore: CanvasStore, sourceViewCache: ICache, threeDViewerCache: Cache, docUrl: cnl) {
		super();
		makeObservable(this);
		this.sourceViewCache = sourceViewCache;
		this.threeDViewerCache = threeDViewerCache;
		this.docUrl = docUrl;
		this.canvasStore = canvasStore;
	}
}